<template>
  <div class="game-board">
    <div class="board-cells">
      <!-- 层叠：底座 < hero < 宝箱 < 文案；同层内用 cell.zIndex 做等距前后遮挡 -->
      <div
        class="board-cell"
        v-for="cell in localeBoardCells"
        :key="`base-${cell.cellIndex}`"
        :style="getCellBaseStyle(cell)"
      >
        <div
          class="board-cell-img"
          :style="{ backgroundImage: `url(${cell.img})` }"
        ></div>
      </div>
      <div
        v-show="heroReady"
        class="hero"
        :class="{ moving: rolling }"
        :style="heroStyle"
      >
        <img src="@/views/act/monopoly/assets/img/hero.png" alt="hero">
      </div>
      <img
        v-for="cell in boxCells"
        :key="`box-${cell.cellIndex}`"
        class="board-cell-box"
        :class="{ absorbed: cell.cellIndex === currCellIndex }"
        :src="cell.boxImg"
        alt="box"
        :style="getCellBoxStyle(cell)"
      >
      <div
        v-for="cell in labelCells"
        :key="`label-${cell.cellIndex}`"
        class="board-cell-label"
        :class="`kind-${getCellMoveKind(cell)}` + ' ' + locale"
        :style="getCellLabelStyle(cell)"
      >
        <i18n-t :keypath="getCellMoveKeypath(cell)" tag="span">
          <template #steps>
            <span class="steps">{{ getCellMoveSteps(cell) }}</span>
          </template>
        </i18n-t>
      </div>
      <div class="cake">
        <img src="@/views/act/monopoly/assets/img/cake.png" alt="cake">
      </div>
    </div>
    <div class="dices">
      <div class="dice-item normal" @click="changeDice(DICE_NORMAL)">
        <img
          :class="{ selected: diceType === DICE_NORMAL }"
          src="@/views/act/monopoly/assets/img/normal-dice.png"
          alt="normal-dice"
        >
      </div>
      <div class="exchange" @click="changeDice()">
        <img src="@/views/act/monopoly/assets/img/exchange.png" alt="exchange">
      </div>
      <div class="dice-item universal" @click="changeDice(DICE_UNIVERSAL)">
        <img
          :class="{ selected: diceType === DICE_UNIVERSAL }"
          src="@/views/act/monopoly/assets/img/universal-dice.png"
          alt="universal-dice"
        >
      </div>
    </div>
    <div class="dice-num" v-if="diceType === DICE_NORMAL">
      {{ $t('monopoly.normalDiceNum', { num: normalDiceNum }) }}
    </div>
    <div class="dice-num" v-else-if="diceType === DICE_UNIVERSAL">
      {{ $t('monopoly.universalDiceNum', { num: universalDiceNum }) }}
    </div>
    <div class="throw-btns">
      <div
        class="throw-once-btn"
        :class="{ disabled: rolling }"
        @click="handleThrowClick"
      >
        <img
          v-if="diceType === DICE_NORMAL"
          class="dice-icon"
          src="@/views/act/monopoly/assets/img/normal-dice.png"
          alt="normal-dice"
        >
        <img
          v-else
          class="dice-icon"
          src="@/views/act/monopoly/assets/img/universal-dice.png"
          alt="universal-dice"
        >
        <div class="throw-text" :class="locale">
          {{ $t('monopoly.throwOnce') }}
        </div>
      </div>
      <div
        class="throw-all-btn"
        v-if="diceType === DICE_NORMAL"
        :class="{ disabled: rolling }"
        @click="handleThrowAllClick"
      >
        <img class="dice-icon" src="@/views/act/monopoly/assets/img/normal-dice.png" alt="normal-dice">
        <div class="throw-text" :class="locale">
          {{ $t('monopoly.throwAll') }}
        </div>
      </div>
    </div>

    <ChooseStepsDialog
      v-model="chooseStepsVisible"
      @confirm="onChooseStepsConfirm"
    />
    <ThrowResultDialog
      v-model="throwResultVisible"
      :steps="throwSteps"
      @confirm="onThrowResultConfirm"
    />
    <RewardDialog
      v-model="rewardVisible"
      :rewards="throwRewards"
    />
    <MysteryEmptyDialog
      v-model="mysteryEmptyVisible"
      :rewards="throwRewards"
    />
    <ThrowAllRewardDialog
      v-model="throwAllRewardVisible"
      :total-steps="throwAllTotalSteps"
      :rewards="throwAllRewards"
    />
    <NoDiceDialog v-model="noDiceVisible" />
  </div>
</template>

<script setup>
import { computed, inject, onUnmounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import MonopolyApi from '@/views/act/monopoly/api'
import { localeBoardCells } from './index.js'
import ChooseStepsDialog from '../ChooseStepsDialog/index.vue'
import ThrowResultDialog from '../ThrowResultDialog/index.vue'
import RewardDialog from '../RewardDialog/index.vue'
import MysteryEmptyDialog from '../MysteryEmptyDialog/index.vue'
import ThrowAllRewardDialog from '../ThrowAllRewardDialog/index.vue'
import NoDiceDialog from '../NoDiceDialog/index.vue'
import {
  trackRollDice,
  trackRollAll,
  trackRollResults,
} from '@/views/act/monopoly/utils/track'

const DICE_NORMAL = 1
const DICE_UNIVERSAL = 2
const ROLL_MODE_SINGLE = 1
const ROLL_MODE_ALL = 2
const CELL_KIND_MYSTERY = 'mystery'
const POOL_NORMAL = 'normal'
const BOARD_CELL_COUNT = 24
/** 掷所有动画最多走两圈 */
const MAX_ROLL_ALL_ANIM_STEPS = BOARD_CELL_COUNT * 2

const CELL_W = 138
const CELL_H = 118
const HERO_W = 106
const HERO_H = 146
const HERO_OFFSET_X = -10
// 每格预留 10 层：底座0 / hero1 / 宝箱2；文案单独提到全局最高带，避免被邻格宝箱挡住
const Z_LAYER_SPAN = 10
const Z_BASE = 0
const Z_HERO = 1
const Z_BOX = 2
const Z_LABEL_FLOOR = 1000
const HERO_OFFSET_Y = -70

const store = useStore()
const { locale } = useI18n()
const activityInfo = inject('monopolyActivityInfo')
const commonConfig = inject('monopolyCommonConfig', null)
const refreshActivityInfo = inject('refreshMonopolyActivityInfo', () => Promise.resolve())
const refreshRankList = inject('refreshMonopolyRankList', () => Promise.resolve())
const openMonopolyLogin = inject('openMonopolyLogin', () => {})
/** 过圈后再延迟拉排行，避开服务端 5s 榜缓存窗口 */
const RANK_REFRESH_AFTER_LAP_MS = 6000
let rankRefreshTimer = null

const clearRankRefreshTimer = () => {
  if (rankRefreshTimer) {
    clearTimeout(rankRefreshTimer)
    rankRefreshTimer = null
  }
}

const hasCrossedLapInRoll = (results, lapsBefore, lapsAfter) => {
  if ((results || []).some((item) => !!item?.crossedLap)) return true
  if (typeof lapsAfter === 'number' && lapsAfter > Number(lapsBefore || 0)) return true
  return false
}

const scheduleRankRefreshIfCrossedLap = (results, lapsBefore, lapsAfter) => {
  if (!hasCrossedLapInRoll(results, lapsBefore, lapsAfter)) return
  clearRankRefreshTimer()
  rankRefreshTimer = setTimeout(() => {
    rankRefreshTimer = null
    refreshRankList()
  }, RANK_REFRESH_AFTER_LAP_MS)
}

onUnmounted(() => {
  clearRankRefreshTimer()
})

const diceType = ref(DICE_NORMAL)
const currCellIndex = ref(0)
const rolling = ref(false)

const chooseStepsVisible = ref(false)
const throwResultVisible = ref(false)
const rewardVisible = ref(false)
const mysteryEmptyVisible = ref(false)
const throwSteps = ref(0)
const throwRewards = ref([])
const throwCellKind = ref('')
const throwPoolType = ref('')
const pendingSingleResult = ref(null)

const throwAllRewardVisible = ref(false)
const throwAllTotalSteps = ref(0)
const throwAllRewards = ref([])
const noDiceVisible = ref(false)

const isLoggedIn = computed(() => !!store.state.actUserInfo?.token)
/** 已登录等 activity-info 回来再显示，避免从起点滑到真实格 */
const heroReady = computed(() => {
  if (!isLoggedIn.value) return true
  return Number(activityInfo?.serverTime) > 0
})
const normalDiceNum = computed(() => activityInfo?.normalDiceCount || 0)
const universalDiceNum = computed(() => activityInfo?.universalDiceCount || 0)

const boardCellMoveStepsMap = computed(() => {
  const map = new Map()
  ;(commonConfig?.boardCells || []).forEach((item) => {
    if (typeof item?.cellIndex === 'number') {
      map.set(item.cellIndex, Number(item.moveSteps || 0))
    }
  })
  return map
})

const getCellMoveKind = (cell) => {
  if (!cell) return ''
  // cellIndex=10 固定按城堡直达文案
  const kind = cell.cellIndex === 10 ? 'castle' : cell.kind
  if (kind !== 'forward' && kind !== 'back' && kind !== 'castle') return ''
  const steps = boardCellMoveStepsMap.value.get(cell.cellIndex) || cell.moveSteps || 0
  if (!steps) return ''
  return kind
}

const getCellMoveSteps = (cell) => {
  const moveSteps = boardCellMoveStepsMap.value.get(cell.cellIndex) || cell.moveSteps || 0
  // 城堡格印刷「直达23格」：展示 1-based 目标格号，而非前进步数
  // (10 + 12) % 24 + 1 = 23
  if ((cell.cellIndex === 10 ? 'castle' : cell.kind) === 'castle') {
    return ((cell.cellIndex + moveSteps) % 24) + 1
  }
  return moveSteps
}

const getCellMoveKeypath = (cell) => {
  const kind = getCellMoveKind(cell)
  if (kind === 'castle') return 'monopoly.moveStepsCastle'
  if (kind === 'back') return 'monopoly.moveStepsBack'
  if (kind === 'forward') return 'monopoly.moveSteps'
  return ''
}

const currCell = computed(() =>
  localeBoardCells.find(c => c.cellIndex === currCellIndex.value)
  || localeBoardCells[0]
)

const getCellLayerZ = (cellZ, layer) => (cellZ || 0) * Z_LAYER_SPAN + layer

const boxCells = computed(() => localeBoardCells.filter((cell) => !!cell.boxImg))
const labelCells = computed(() => localeBoardCells.filter((cell) => !!getCellMoveKind(cell)))

const getCellBaseStyle = (cell) => ({
  top: `${cell.position.y / 75}rem`,
  left: `${cell.position.x / 75}rem`,
  zIndex: getCellLayerZ(cell.zIndex, Z_BASE),
})

const getCellBoxStyle = (cell) => ({
  left: `${(cell.position.x + CELL_W * 0.53) / 75}rem`,
  top: `${(cell.position.y + CELL_H * 0.22) / 75}rem`,
  zIndex: getCellLayerZ(cell.zIndex, Z_BOX),
})

const getCellLabelStyle = (cell) => ({
  left: `${(cell.position.x + CELL_W * 0.5) / 75}rem`,
  top: `${(cell.position.y + CELL_H * 0.42) / 75}rem`,
  // 文案全局置顶，仅在文案之间保留等距前后顺序
  zIndex: Z_LABEL_FLOOR + (cell.zIndex || 0),
})

const heroStyle = computed(() => {
  const { x, y } = currCell.value.position
  return {
    left: `${(x + (CELL_W - HERO_W) / 2 + HERO_OFFSET_X) / 75}rem`,
    top: `${(y + (CELL_H - HERO_H) / 2 + HERO_OFFSET_Y) / 75}rem`,
    zIndex: getCellLayerZ(currCell.value.zIndex, Z_HERO),
  }
})

const syncPositionFromActivity = () => {
  if (activityInfo && typeof activityInfo.position === 'number') {
    currCellIndex.value = activityInfo.position
  }
}

watch(
  () => activityInfo?.position,
  () => {
    if (!rolling.value) {
      syncPositionFromActivity()
    }
  },
  { immediate: true }
)

const changeDice = (type) => {
  if (!type) {
    diceType.value = diceType.value === DICE_NORMAL ? DICE_UNIVERSAL : DICE_NORMAL
  } else if (type === DICE_NORMAL) {
    diceType.value = DICE_NORMAL
  } else if (type === DICE_UNIVERSAL) {
    diceType.value = DICE_UNIVERSAL
  }
}

const requireLogin = () => {
  if (!isLoggedIn.value) {
    openMonopolyLogin()
    return false
  }
  return true
}

const openNoDiceDialog = () => {
  noDiceVisible.value = true
}

const handleThrowClick = () => {
  if (rolling.value) return
  if (!requireLogin()) return

  if (diceType.value === DICE_NORMAL) {
    if (normalDiceNum.value <= 0) {
      openNoDiceDialog()
      return
    }
    doRoll({ diceType: DICE_NORMAL, mode: ROLL_MODE_SINGLE, chosenSteps: 0 })
    return
  }

  if (universalDiceNum.value <= 0) {
    openNoDiceDialog()
    return
  }
  chooseStepsVisible.value = true
}

const handleThrowAllClick = () => {
  if (rolling.value) return
  if (!requireLogin()) return
  if (normalDiceNum.value <= 0) {
    openNoDiceDialog()
    return
  }
  doRoll({ diceType: DICE_NORMAL, mode: ROLL_MODE_ALL, chosenSteps: 0 })
}

const onChooseStepsConfirm = (steps) => {
  doRoll({
    diceType: DICE_UNIVERSAL,
    mode: ROLL_MODE_SINGLE,
    chosenSteps: steps,
  })
}

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

const dedupePath = (path) => {
  const uniquePath = []
  path.forEach((pos) => {
    if (typeof pos !== 'number') return
    if (uniquePath[uniquePath.length - 1] !== pos) {
      uniquePath.push(pos)
    }
  })
  return uniquePath
}

const normalizeCellIndex = (pos) => ((pos % BOARD_CELL_COUNT) + BOARD_CELL_COUNT) % BOARD_CELL_COUNT

/**
 * 掷所有动画步数：≤48 原样；>48 按整圈 24 往下减到 ≤48
 * 例：49→25，50→26，72→48，73→25
 */
const getRollAllAnimStepCount = (totalSteps) => {
  const s = Math.max(0, Number(totalSteps) || 0)
  if (s <= MAX_ROLL_ALL_ANIM_STEPS) return s
  return ((s - 1) % BOARD_CELL_COUNT) + BOARD_CELL_COUNT + 1
}

/** 取掷所有最终落点（服务端保证最终停点不是前进/后退/直达格） */
const resolveRollAllFinalPos = (results) => {
  const list = results || []
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (typeof list[i]?.finalPosition === 'number') {
      return normalizeCellIndex(list[i].finalPosition)
    }
  }
  if (typeof activityInfo?.position === 'number') {
    return normalizeCellIndex(activityInfo.position)
  }
  return null
}

/**
 * 将期望动画步数对齐到最终落点：只顺时针走，且 (start + steps) % 24 === final
 * 超过两圈时先压缩到 ≤48，再微调整圈数保证落点正确
 */
const alignRollAllAnimSteps = (startPos, finalPos, totalSteps) => {
  const start = normalizeCellIndex(startPos)
  const end = normalizeCellIndex(finalPos)
  const distance = (end - start + BOARD_CELL_COUNT) % BOARD_CELL_COUNT
  const raw = Math.max(0, Number(totalSteps) || 0)

  if (raw <= 0) return distance

  let steps = raw > MAX_ROLL_ALL_ANIM_STEPS
    ? getRollAllAnimStepCount(raw)
    : raw

  const rem = steps % BOARD_CELL_COUNT
  if (rem !== distance) {
    let k = Math.floor(steps / BOARD_CELL_COUNT)
    let candidate = k * BOARD_CELL_COUNT + distance
    if (candidate > MAX_ROLL_ALL_ANIM_STEPS) {
      candidate -= BOARD_CELL_COUNT
    }
    if (candidate <= 0) {
      candidate = distance === 0 ? BOARD_CELL_COUNT : distance
    }
    steps = candidate
  }

  if (steps <= 0 && distance > 0) return distance
  if (steps <= 0 && raw > 0 && distance === 0) return BOARD_CELL_COUNT
  return steps
}

/** 从当前位置顺时针走 stepCount 格（不含起点，含终点） */
const buildClockwisePathFrom = (startPos, stepCount) => {
  const path = []
  let pos = normalizeCellIndex(startPos)
  const n = Math.max(0, stepCount)
  for (let i = 0; i < n; i += 1) {
    pos = normalizeCellIndex(pos + 1)
    path.push(pos)
  }
  return path
}

// 单次投掷：含前进/后退/直达效果路径
const moveHeroAlong = async (result) => {
  const path = []
  if (Array.isArray(result.passPositions)) {
    path.push(...result.passPositions)
  }
  if (typeof result.landPosition === 'number') {
    path.push(result.landPosition)
  }
  if (
    typeof result.effectTarget === 'number'
    && result.effectTarget >= 0
    && result.effectTarget !== result.landPosition
  ) {
    if (Array.isArray(result.effectPassPositions)) {
      path.push(...result.effectPassPositions)
    }
    path.push(result.effectTarget)
  }
  if (typeof result.finalPosition === 'number') {
    path.push(result.finalPosition)
  }

  for (const pos of dedupePath(path)) {
    currCellIndex.value = pos
    await sleep(200)
  }

  if (typeof result.finalPosition === 'number') {
    currCellIndex.value = result.finalPosition
  }
}

/**
 * 掷所有动画：不回放每把骰的落点/效果（会拼出逆时针跳格），
 * 只取最终落点，再从当前格顺时针一格格走到终点；超过两圈压缩步数。
 */
const moveHeroAlongRollAll = async (results, totalSteps) => {
  const startPos = normalizeCellIndex(currCellIndex.value)
  const finalPos = resolveRollAllFinalPos(results)
  if (typeof finalPos !== 'number') return

  const animSteps = alignRollAllAnimSteps(startPos, finalPos, totalSteps)
  const path = buildClockwisePathFrom(startPos, animSteps)

  for (const pos of path) {
    currCellIndex.value = pos
    await sleep(160)
  }

  currCellIndex.value = finalPos
}

const mergeRewards = (results) => {
  const map = new Map()
  ;(results || []).forEach((result) => {
    (result.rewards || []).forEach((item) => {
      if (!item?.propId && !item?.propName) return
      const key = item.propId || item.propName
      const prev = map.get(key)
      if (prev) {
        prev.propCount += item.propCount || 0
      } else {
        map.set(key, {
          propId: item.propId,
          propName: item.propName,
          propCount: item.propCount || 0,
        })
      }
    })
  })
  return Array.from(map.values())
}

const applyRollResp = (res) => {
  if (!res || !activityInfo) return
  activityInfo.normalDiceCount = res.normalDiceCount
  activityInfo.universalDiceCount = res.universalDiceCount
  activityInfo.position = res.position
  if (typeof res.totalLaps === 'number') {
    activityInfo.totalLaps = res.totalLaps
  }
  // 投掷入背包的奖励会进待提取，需同步本地计数，否则提取按钮会误判为空
  const added = (res.results || []).reduce((sum, item) => {
    return sum + (item.rewards?.length || 0)
  }, 0)
  if (added > 0) {
    activityInfo.pendingRewardCount = Number(activityInfo.pendingRewardCount || 0) + added
    activityInfo.extractRedDot = true
  }
}

const doRoll = async (params) => {
  if (rolling.value) return
  rolling.value = true
  try {
    const lapsBefore = Number(activityInfo?.totalLaps || 0)
    // dice_remain：投前剩余
    const diceRemainBefore = Number(
      params.diceType === DICE_UNIVERSAL
        ? activityInfo?.universalDiceCount || 0
        : activityInfo?.normalDiceCount || 0,
    )
    const res = await MonopolyApi.roll(params)
    applyRollResp(res)
    const results = res?.results || []
    scheduleRankRefreshIfCrossedLap(results, lapsBefore, res?.totalLaps)
    if (!results.length) {
      await refreshActivityInfo()
      return
    }

    const isRollAll = params.mode === ROLL_MODE_ALL
    if (isRollAll) {
      trackRollAll({ diceCount: results.length, diceRemain: diceRemainBefore })
    } else {
      trackRollDice({
        diceType: params.diceType,
        rollNumber: results[0]?.steps || params.chosenSteps || 0,
        diceRemain: diceRemainBefore,
      })
    }
    trackRollResults(results, commonConfig)

    if (isRollAll) {
      throwAllTotalSteps.value = res.totalSteps || results.reduce((sum, r) => sum + (r.steps || 0), 0)
      throwAllRewards.value = mergeRewards(results)
      await moveHeroAlongRollAll(results, throwAllTotalSteps.value)
      throwAllRewardVisible.value = true
      rolling.value = false
    } else {
      const result = results[0]
      throwSteps.value = result.steps || 0
      throwRewards.value = result.rewards || []
      throwCellKind.value = result.cellKind || ''
      throwPoolType.value = result.poolType || ''
      pendingSingleResult.value = result
      // 先弹窗播动画，结束后再走动；rolling 保持到走动结束
      throwResultVisible.value = true
    }
  } catch (e) {
    console.error(e)
    await refreshActivityInfo()
    rolling.value = false
  }
}

const openRewardAfterThrow = () => {
  if (!throwRewards.value.length) return
  // 神秘格已领过大奖后再次踩中：走普通奖池，展示「宝箱已空」弹窗
  if (throwCellKind.value === CELL_KIND_MYSTERY && throwPoolType.value === POOL_NORMAL) {
    mysteryEmptyVisible.value = true
    return
  }
  rewardVisible.value = true
}

const onThrowResultConfirm = async () => {
  const result = pendingSingleResult.value
  pendingSingleResult.value = null
  try {
    if (result) {
      await moveHeroAlong(result)
    }
    openRewardAfterThrow()
  } finally {
    rolling.value = false
  }
}
</script>

<style lang="less" scoped>
.game-board {
  margin-top: 80px;
  position: relative;
  text-align: center;
  .board-cells {
    position: relative;
    height: 775px;
    .board-cell {
      position: absolute;
      width: 138px;
      height: 118px;
      font-size: 24px;

      .board-cell-img {
        width: 100%;
        height: 100%;
        background-size: 100% 100%;
        background-repeat: no-repeat;
        filter: drop-shadow(-5px 12px 0 rgba(0, 0, 0, 0.15));
      }
    }

    .board-cell-box {
      position: absolute;
      width: 120px;
      height: auto;
      transform: translate(-50%, -50%) scale(1);
      transform-origin: center center;
      pointer-events: none;
      transition: transform 0.28s ease-in;

      &.absorbed {
        transform: translate(-50%, -50%) scale(0);
      }
    }

    .board-cell-label {
      position: absolute;
      width: 138px;
      padding: 2px 4px;
      box-sizing: border-box;
      transform: translate(-50%, -50%);
      font-size: 24px;
      font-weight: bold;
      line-height: 1.25;
      color: #fff;
      text-align: center;
      pointer-events: none;
      text-shadow:
        0 0 2px #1B2B4B,
        0 0 2px #1B2B4B,
        1px 1px 0 #1B2B4B,
        -1px -1px 0 #1B2B4B;

      .steps {
        color: #FFA352;
      }

      &.kind-castle {
        color: #15FFC5;

        .steps {
          color: #FFF555;
        }
      }
      &.hk, &.tw {
        white-space: pre-line;
      }
    }

    .hero {
      position: absolute;
      width: 106px;
      height: 146px;
      pointer-events: none;
      img {
        width: 100%;
        height: 100%;
      }
      &.moving {
        transition: left 0.16s ease-in-out, top 0.16s ease-in-out;
      }
    }
    .cake {
      width: 310px;
      height: 391px;
      position: absolute;
      top: 120px;
      left: 220px;
      z-index: 200;
      img {
        width: 100%;
        height: 100%;
      }
    }
  }
  .dices {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    .dice-item {
      width: 127px;
      height: 131px;
      background: url('~@/views/act/monopoly/assets/img/normal-dice-bg.png') no-repeat center center / cover;
      display: flex;
      justify-content: center;
      align-items: center;
      img {
        width: 83px;
        height: 91px;
        object-fit: contain;
        margin-bottom: 10px;
        transition: transform 0.2s ease;
        &.selected {
          transform: scale(1.5);
        }
      }
    }
    .exchange {
      width: 61px;
      height: 63px;
      img {
        width: 100%;
        height: 100%;
      }
    }
  }
  .dice-num {
    display: inline-block;
    margin: 0 auto;
    border: 1px solid #fff;
    height: 52px;
    line-height: 52px;
    text-align: center;
    min-width: 320px;
    font-size: 24px;
    color: #0C0C0C;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 26px;
    padding: 0 30px;
    margin-top: 10px;
  }
  .throw-btns {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 30px;
    .throw-once-btn, .throw-all-btn {
      display: inline-block;
      padding: 0 30px 0 90px;
      position: relative;
      width: 290px;
      height: 98px;
      background: url('~@/views/act/monopoly/assets/img/throw-once-btn-bg.png') no-repeat center center / 100% 100%;
      margin-top: 25px;
      cursor: pointer;
      &.disabled {
        opacity: 0.6;
        pointer-events: none;
      }
      .dice-icon {
        height: 63px;
        position: absolute;
        top: 20px;
        left: 30px;
      }
      .throw-text {
        width: 190px;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        font-weight: bold;
        font-size: 34px;
        color: #fff;
        &.id {
          font-size: 30px;
        }
      }
    }
    .throw-all-btn {
      background: url('~@/views/act/monopoly/assets/img/throw-all-btn-bg.png') no-repeat center center / 100% 100%;
      .throw-text {
        color: #B76916;
      }
    }
  }
}
</style>
